import { bookingInquiryStrings } from '@data/bookingInquiry';
import { instructionStrings } from '@data/instruction';
import { TruckName } from '@models/next/BookingInquiryEnums';
import { Company } from '@models/next/Company';
import { BookingInquiryContact } from '@models/next/Contact';
import { DeliveryRequest } from '@models/next/DeliveryRequest';
import { InstructionStatus, InstructionStatusName } from '@models/next/Instruction';
import { PaginationType } from '@models/next/PaginationType';
import { AdminUser } from '@models/next/User';
import { f } from '@utils/nextForms';
import { z } from 'zod';

// For value '', means "unselected"
export const DeliveryTruckName = z.enum(['', 'その他', ...TruckName.options]);

export type DeliveryTruckName = z.infer<typeof DeliveryTruckName>;

export const DeliveryTruck = z.object({
  name: DeliveryTruckName,
  delivery_fee: z.number(), // non_target_tax_price + target_tax_price
  delivery_date: f.dateString(),
  delivery_time: z.string(), // Example: 配達当日朝9:00, any string
  consignee_price: z.number(),
  shipper_price: z.number(),
});

export type DeliveryTruck = z.infer<typeof DeliveryTruck>;

export const DeliveryDateOptions = z
  .object({
    '': f.dateString().array(),
    その他: f.dateString().array(),
    mix: f.dateString().array(),
    '4T': f.dateString().array(),
    '4Tx2': f.dateString().array(),
    '10T': f.dateString().array(),
  })
  .partial();

export const DeliveryInstructionFields = {
  is_receiver_checked: z.boolean().label(bookingInquiryStrings.receiver),
  receiver: BookingInquiryContact.nullable(),
  old_receiver: BookingInquiryContact,
  truck: DeliveryTruckName.label(instructionStrings.truckChoice),
  truck_options: DeliveryTruck.array(),
  delivery_date_options: DeliveryDateOptions,
  preferred_delivery_date: f
    .dateString()
    .nullable()
    .label(instructionStrings.preferredDeliveryDate),
  /**
   * Choices are from instructionStrings.deliveryTimeOptions
   */
  preferred_delivery_time: z.string().label(instructionStrings.deliveryTime),
  has_special_note: z.boolean().label(instructionStrings.specialNote),
  special_note: z.string().label(instructionStrings.specialNoteDetails),
  writer_id: z.number().nullish(),
  writer: AdminUser.nullish(),
  writer_company: Company.nullish(),
  cutoff_date: f.dateString(),
  status: InstructionStatus.array(),
  status_name: InstructionStatusName.label(instructionStrings.status),
  simulator_error: z.string().nullish(),
  cargo_changed: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
} satisfies Record<string, z.ZodType>;

export const DeliveryInstruction = z.object({
  is_receiver_checked: DeliveryInstructionFields.is_receiver_checked,
  receiver: DeliveryInstructionFields.receiver,
  truck: DeliveryInstructionFields.truck,
  preferred_delivery_date: DeliveryInstructionFields.preferred_delivery_date,
  preferred_delivery_time: DeliveryInstructionFields.preferred_delivery_time,
  has_special_note: DeliveryInstructionFields.has_special_note,
  special_note: DeliveryInstructionFields.special_note,
  deadline: DeliveryInstructionFields.cutoff_date,
  status: DeliveryInstructionFields.status,
  old_receiver: DeliveryInstructionFields.old_receiver,
  truck_options: DeliveryInstructionFields.truck_options,
  delivery_date_options: DeliveryInstructionFields.delivery_date_options,
  created_at: DeliveryInstructionFields.created_at,
  updated_at: DeliveryInstructionFields.updated_at,
  writer_id: DeliveryInstructionFields.writer_id,
  simulator_error: DeliveryInstructionFields.simulator_error,
  cargo_changed: DeliveryInstructionFields.cargo_changed,
  delivery_requests: DeliveryRequest.array(),
});

export type DeliveryInstruction = z.infer<typeof DeliveryInstruction>;

export const DeliveryInstructionAdmin = z.object({
  ...DeliveryInstruction.shape,
  writer: DeliveryInstructionFields.writer.nullish(),
  writer_company: DeliveryInstructionFields.writer_company.nullish(),
});

export type DeliveryInstructionAdmin = z.infer<typeof DeliveryInstructionAdmin>;

export const DeliveryInstructionListItem = z.object({
  d2d_no: z.string(),
  deadline: DeliveryInstructionFields.cutoff_date,
  status: DeliveryInstructionFields.status,
  eta: z.string().nullish(),
  etd: z.string().nullish(),
  created_at: DeliveryInstructionFields.created_at,
  updated_at: DeliveryInstructionFields.updated_at,
});

export type DeliveryInstructionListItem = z.infer<typeof DeliveryInstructionListItem>;

export const DeliveryInstructionListItemPages = z.object({
  count: z.number(),
  results: DeliveryInstructionListItem.array(),
});

export type DeliveryInstructionListItemPages = z.infer<typeof DeliveryInstructionListItemPages>;

export const PatchDeliveryInstruction = z
  .object({
    is_receiver_checked: DeliveryInstructionFields.is_receiver_checked,
    receiver: DeliveryInstructionFields.receiver,
    truck: DeliveryInstructionFields.truck,
    preferred_delivery_date: DeliveryInstructionFields.preferred_delivery_date,
    preferred_delivery_time: DeliveryInstructionFields.preferred_delivery_time,
    has_special_note: DeliveryInstructionFields.has_special_note,
    special_note: DeliveryInstructionFields.special_note,
    status_name: InstructionStatusName,
  })
  .partial();

export type PatchDeliveryInstruction = z.infer<typeof PatchDeliveryInstruction>;

export const DeliveryInstructionSearchQuery = z.object({
  page: PaginationType.shape.page,
  page_size: PaginationType.shape.page_size,
  d2d_no: z.string().label(bookingInquiryStrings.d2dNo),
  status: DeliveryInstructionFields.status_name.array(),
});

export type DeliveryInstructionSearchQuery = z.infer<typeof DeliveryInstructionSearchQuery>;
